import {
  carLabelForKind,
  DUTY_CERT_703_LABEL,
  DUTY_CERT_703_LABEL_LEGACY,
  DUTY_CERT_704_LABEL,
  migrateCompanyCarCertificate,
  type CarCertificateKind,
} from "./carOps";
import { appNow } from "./appClock";
import { dualClockStamp, halifaxParts } from "./clock";
import { blockTimeMin } from "./flightlog";
import type { DutyTablePresetId, FlightLogEntry, Mission, PilotProfile } from "./types";

export { DUTY_CERT_703_LABEL, DUTY_CERT_703_LABEL_LEGACY, DUTY_CERT_704_LABEL };

export type DutyStatus = "off" | "on" | "rest";

export type DutyLevel = "ok" | "warn" | "alert";

export type DutyEventKind =
  | "start_duty"
  | "end_duty"
  | "start_rest"
  | "reset_timer"
  | "pause_duty"
  | "resume_duty";

/** Selectable in Settings. Celtic Air default is 704. */
export type DutyCertificateKind = CarCertificateKind;

/**
 * Public-release legal banner. Kept for a future public build — not shown on the
 * private hobby desk. Flip `SHOW_PUBLIC_SIM_DISCLAIMERS` to restore it.
 */
export const DUTY_SIM_DISCLAIMER =
  "Sim / training aid — not legal compliance. Celtic Air default is CAR 704 Commuter. The dispatcher can pick 703 / 704 / 705. The duty clock is Start / End / Rest plus log credit — not a Transport Canada table.";

/** Off for the private desk. Set true to restore sim-only / not-for-real-world banners. */
export const SHOW_PUBLIC_SIM_DISCLAIMERS = false;

/**
 * Training-aid presets per CAR kind — not Transport Canada tables.
 * Numbers are distinct so the Settings picker actually swaps the clock.
 */
export const DUTY_LIMIT_PRESETS: Record<DutyCertificateKind, DutyLimits> = {
  /** Classic air-taxi-shaped ballpark (tighter daily flight than 704). */
  "703": { maxFdpMin: 14 * 60, maxFlightTimeMin: 8 * 60, minRestMin: 10 * 60, warnRatio: 0.8 },
  /** Celtic Air default — sim-friendly 704 commuter ballpark. */
  "704": { maxFdpMin: 14 * 60, maxFlightTimeMin: 8 * 60 + 30, minRestMin: 10 * 60, warnRatio: 0.8 },
  /** Airline-shaped: shorter FDP, longer min rest. */
  "705": { maxFdpMin: 13 * 60, maxFlightTimeMin: 8 * 60, minRestMin: 12 * 60, warnRatio: 0.8 },
};

/** 704 preset — 14:00 FDP, 8:30 flight, 10:00 rest. */
export const DEFAULT_DUTY_LIMITS: DutyLimits = DUTY_LIMIT_PRESETS["704"];

export type DutyLimits = {
  /** Max flight duty period. Default 14:00. */
  maxFdpMin: number;
  /** Max flight time in the duty day. Default 8:30. */
  maxFlightTimeMin: number;
  /** Min rest before next duty. Default 10:00. */
  minRestMin: number;
  /** Soft warn at this fraction of FDP / flight / rest. Default 0.80. */
  warnRatio: number;
};

export type DutySettings = {
  certificateLabel: string;
  certificateKind: DutyCertificateKind;
  limits: DutyLimits;
  /** True after the dispatcher picks 703 / 704 / 705 in Settings. */
  certificateUserSet?: boolean;
  /**
   * Active duty-table preset. CAR ids follow the company certificate.
   * FAR / EASA ids change the clock without rewriting that certificate.
   */
  dutyTablePresetId?: DutyTablePresetId;
  /**
   * Name shown on the duty strip and assign gates. FAR / EASA presets set this
   * so the strip does not keep saying "704 Commuter" while using other hours.
   */
  clockLabel?: string;
};

const CAR_PRESET_ID: Record<DutyCertificateKind, DutyTablePresetId> = {
  "703": "car703",
  "704": "car704",
  "705": "car705",
};

export const DEFAULT_DUTY_SETTINGS: DutySettings = {
  certificateLabel: DUTY_CERT_704_LABEL,
  certificateKind: "704",
  limits: { ...DEFAULT_DUTY_LIMITS },
  certificateUserSet: false,
  dutyTablePresetId: "car704",
  clockLabel: DUTY_CERT_704_LABEL,
};

/** Apply a picker choice: kind + stock label + that kind’s limit preset. */
export function applyCarCertificate(kind: DutyCertificateKind): DutySettings {
  const label = carLabelForKind(kind);
  return {
    certificateKind: kind,
    certificateLabel: label,
    limits: { ...DUTY_LIMIT_PRESETS[kind] },
    certificateUserSet: true,
    dutyTablePresetId: CAR_PRESET_ID[kind],
    clockLabel: label,
  };
}

export type DutyEvent = {
  id: string;
  kind: DutyEventKind;
  atUtc: string;
  atLocal: string;
};

export type DutySession = {
  id: string;
  crewId: string;
  crewName: string;
  status: DutyStatus;
  startedAtUtc: string;
  startedAtLocal: string;
  endedAtUtc?: string;
  endedAtLocal?: string;
  restStartedAtUtc?: string;
  restStartedAtLocal?: string;
  restEndedAtUtc?: string;
  restEndedAtLocal?: string;
  /**
   * When set, the open ON duty timer is paused at this stamp.
   * FDP freezes until resume / end / rest. Persists across refresh / sync.
   */
  pausedAtUtc?: string;
  pausedAtLocal?: string;
  /** Completed pause minutes already subtracted from FDP (closed pause intervals). */
  pauseAccumMin: number;
  /** Minutes credited from completed flight logs while this session was on duty. */
  flightTimeMin: number;
  creditedLogIds: string[];
  events: DutyEvent[];
  /** Duty started before min rest was complete (sim flag, not a lockout). */
  restShort?: boolean;
};

export type DutyRoster = {
  settings: DutySettings;
  sessions: DutySession[];
};

export type DutyCrew = {
  id: string;
  name: string;
};

export type DutyView = {
  crewId: string;
  crewName: string;
  status: DutyStatus;
  statusLabel: "ON DUTY" | "OFF DUTY" | "REST" | "PAUSED";
  /** True when status is on and the open session has pausedAtUtc. */
  paused: boolean;
  fdpElapsedMin: number;
  fdpRemainMin: number;
  flightTimeTodayMin: number;
  flightRemainMin: number;
  restElapsedMin: number | null;
  restRemainMin: number | null;
  timeToLimitMin: number | null;
  level: DutyLevel;
  restShort: boolean;
  lastEvent?: DutyEvent;
  session?: DutySession;
  limits: DutyLimits;
  certificateLabel: string;
};

export function emptyDutyRoster(): DutyRoster {
  return { settings: { ...DEFAULT_DUTY_SETTINGS, limits: { ...DEFAULT_DUTY_LIMITS } }, sessions: [] };
}

export function crewIdFromName(name: string): string {
  const slug = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return slug || "emerson";
}

export function formatDutyHours(min: number): string {
  const sign = min < 0 ? "-" : "";
  const abs = Math.abs(Math.round(min));
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return `${sign}${h}:${String(m).padStart(2, "0")}`;
}

/** Accepts `14:00`, `8:30`, or a decimal-hours number (`8.5`). */
export function parseDutyHours(value: string, fallbackMin: number): number {
  const t = value.trim();
  const hm = /^(-)?(\d{1,2}):(\d{2})$/.exec(t);
  if (hm) {
    const hours = Number(hm[2]);
    const minutes = Number(hm[3]);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes) || minutes > 59) return fallbackMin;
    const total = hours * 60 + minutes;
    return hm[1] ? -total : total;
  }
  const n = Number(t);
  if (Number.isFinite(n) && n >= 0) return Math.round(n * 60);
  return fallbackMin;
}

function clampMin(n: unknown, fallback: number, max = 36 * 60): number {
  if (typeof n !== "number" || !Number.isFinite(n) || n <= 0) return fallback;
  return Math.min(Math.round(n), max);
}

function clampRatio(n: unknown, fallback: number): number {
  if (typeof n !== "number" || !Number.isFinite(n)) return fallback;
  return Math.min(0.99, Math.max(0.1, n));
}

export function hydrateDutyLimits(raw?: Partial<DutyLimits>, fallback: DutyLimits = DEFAULT_DUTY_LIMITS): DutyLimits {
  return {
    maxFdpMin: clampMin(raw?.maxFdpMin, fallback.maxFdpMin),
    maxFlightTimeMin: clampMin(raw?.maxFlightTimeMin, fallback.maxFlightTimeMin),
    minRestMin: clampMin(raw?.minRestMin, fallback.minRestMin),
    warnRatio: clampRatio(raw?.warnRatio, fallback.warnRatio),
  };
}

export function hydrateDutySettings(raw?: Partial<DutySettings>): DutySettings {
  const migrated = migrateCompanyCarCertificate({
    certificateKind: raw?.certificateKind,
    certificateLabel: raw?.certificateLabel,
    certificateUserSet: raw?.certificateUserSet,
  });
  return {
    certificateLabel: migrated.certificateLabel,
    certificateKind: migrated.certificateKind,
    certificateUserSet: migrated.certificateUserSet,
    limits: hydrateDutyLimits(raw?.limits, DUTY_LIMIT_PRESETS[migrated.certificateKind]),
    dutyTablePresetId: isDutyTablePresetId(raw?.dutyTablePresetId)
      ? raw.dutyTablePresetId
      : CAR_PRESET_ID[migrated.certificateKind],
    clockLabel:
      typeof raw?.clockLabel === "string" && raw.clockLabel.trim()
        ? raw.clockLabel.trim()
        : migrated.certificateLabel,
  };
}

const DUTY_TABLE_PRESET_ID_SET = new Set<string>([
  "car703",
  "car704",
  "car705",
  "far135-single",
  "far135-two",
  "far135-scheduled",
  "easa-basic",
  "easa-late",
  "easa-augmented",
]);

function isDutyTablePresetId(value: unknown): value is DutyTablePresetId {
  return typeof value === "string" && DUTY_TABLE_PRESET_ID_SET.has(value);
}

export function hydrateDutyRoster(raw?: Partial<DutyRoster>): DutyRoster {
  const sessions = Array.isArray(raw?.sessions) ? raw.sessions.map(hydrateDutySession).filter(Boolean) : [];
  return {
    settings: hydrateDutySettings(raw?.settings),
    sessions,
  };
}

function hydrateDutySession(raw: DutySession): DutySession {
  const pauseAccumMin = Number.isFinite(raw.pauseAccumMin) ? Math.max(0, Math.round(raw.pauseAccumMin)) : 0;
  const pausedAtUtc = typeof raw.pausedAtUtc === "string" && raw.pausedAtUtc.trim() ? raw.pausedAtUtc.trim() : undefined;
  const pausedAtLocal =
    typeof raw.pausedAtLocal === "string" && raw.pausedAtLocal.trim() ? raw.pausedAtLocal.trim() : undefined;
  return {
    ...raw,
    flightTimeMin: Number.isFinite(raw.flightTimeMin) ? raw.flightTimeMin : 0,
    pauseAccumMin,
    pausedAtUtc: raw.status === "on" ? pausedAtUtc : undefined,
    pausedAtLocal: raw.status === "on" ? pausedAtLocal : undefined,
    creditedLogIds: Array.isArray(raw.creditedLogIds) ? raw.creditedLogIds : [],
    events: Array.isArray(raw.events) ? raw.events : [],
    status: raw.status === "on" || raw.status === "rest" || raw.status === "off" ? raw.status : "off",
  };
}

export function halifaxDateKey(date: Date): string {
  if (!Number.isFinite(date.getTime())) return "";
  const p = halifaxParts(date);
  return `${p.year}-${p.month}-${p.day}`;
}

function elapsedMin(fromUtc: string, to: Date): number {
  const start = new Date(fromUtc).getTime();
  if (!Number.isFinite(start)) return 0;
  return Math.max(0, Math.round((to.getTime() - start) / 60_000));
}

function newDutyId(prefix: string, now: Date): string {
  return `${prefix}-${now.getTime().toString(36)}`;
}

function event(kind: DutyEventKind, now: Date): DutyEvent {
  const stamp = dualClockStamp(now);
  return { id: newDutyId("de", now), kind, atUtc: stamp.utc, atLocal: stamp.local };
}

export function sessionsForCrew(sessions: DutySession[], crewId: string): DutySession[] {
  return sessions.filter((s) => s.crewId === crewId);
}

export function currentSession(sessions: DutySession[], crewId: string): DutySession | undefined {
  return sessions.find((s) => s.crewId === crewId && (s.status === "on" || s.status === "rest"));
}

export function latestSession(sessions: DutySession[], crewId: string): DutySession | undefined {
  return sessions.find((s) => s.crewId === crewId);
}

function replaceSession(sessions: DutySession[], next: DutySession): DutySession[] {
  const idx = sessions.findIndex((s) => s.id === next.id);
  if (idx === -1) return [next, ...sessions];
  return sessions.map((s, i) => (i === idx ? next : s));
}

function closeRest(session: DutySession, now: Date): DutySession {
  if (session.status !== "rest" || session.restEndedAtUtc) return session;
  const stamp = dualClockStamp(now);
  return {
    ...session,
    status: "off",
    restEndedAtUtc: stamp.utc,
    restEndedAtLocal: stamp.local,
    endedAtUtc: session.endedAtUtc ?? stamp.utc,
    endedAtLocal: session.endedAtLocal ?? stamp.local,
  };
}

/** Informal rest: closed rest period, or time since last duty ended. */
export function lastRestMin(sessions: DutySession[], crewId: string, now: Date): number | null {
  const last = latestSession(sessions, crewId);
  if (!last) return null;
  if (last.status === "rest" && last.restStartedAtUtc) {
    return elapsedMin(last.restStartedAtUtc, now);
  }
  if (last.restStartedAtUtc && last.restEndedAtUtc) {
    return elapsedMin(last.restStartedAtUtc, new Date(last.restEndedAtUtc));
  }
  if (last.status === "off" && last.endedAtUtc) {
    return elapsedMin(last.endedAtUtc, now);
  }
  return null;
}

export function startDuty(
  roster: DutyRoster,
  input: { crewId: string; crewName: string; now?: Date },
): DutyRoster {
  const now = input.now ?? appNow();
  const existing = currentSession(roster.sessions, input.crewId);
  if (existing?.status === "on") return roster;

  let sessions = roster.sessions;
  if (existing?.status === "rest") {
    sessions = replaceSession(sessions, closeRest(existing, now));
  }

  const restMin = lastRestMin(sessions, input.crewId, now);
  const restShort = restMin != null && restMin < roster.settings.limits.minRestMin;
  const stamp = dualClockStamp(now);
  const ev = event("start_duty", now);
  const session: DutySession = {
    id: newDutyId("duty", now),
    crewId: input.crewId,
    crewName: input.crewName,
    status: "on",
    startedAtUtc: stamp.utc,
    startedAtLocal: stamp.local,
    flightTimeMin: 0,
    pauseAccumMin: 0,
    creditedLogIds: [],
    events: [ev],
    restShort,
  };
  return { ...roster, sessions: [session, ...sessions] };
}

export function endDuty(
  roster: DutyRoster,
  input: { crewId: string; now?: Date },
): DutyRoster {
  const now = input.now ?? appNow();
  const current = currentSession(roster.sessions, input.crewId);
  if (!current) return roster;
  const stamp = dualClockStamp(now);
  const ev = event("end_duty", now);
  const finalized = finalizeOpenPause(current, now);
  let next: DutySession = {
    ...finalized,
    status: "off",
    endedAtUtc: finalized.endedAtUtc ?? stamp.utc,
    endedAtLocal: finalized.endedAtLocal ?? stamp.local,
    pausedAtUtc: undefined,
    pausedAtLocal: undefined,
    events: [ev, ...finalized.events],
  };
  if (current.status === "rest" && !current.restEndedAtUtc) {
    next = { ...next, restEndedAtUtc: stamp.utc, restEndedAtLocal: stamp.local };
  }
  return { ...roster, sessions: replaceSession(roster.sessions, next) };
}

/**
 * Freeze the open ON duty FDP clock. Rest and off are unchanged.
 * Pause state rides the session (and Nanode snapshot) like start / end.
 */
export function pauseDuty(
  roster: DutyRoster,
  input: { crewId: string; now?: Date },
): DutyRoster {
  const now = input.now ?? appNow();
  const current = currentSession(roster.sessions, input.crewId);
  if (!current || current.status !== "on" || current.pausedAtUtc) return roster;
  const stamp = dualClockStamp(now);
  const ev = event("pause_duty", now);
  const next: DutySession = {
    ...current,
    pausedAtUtc: stamp.utc,
    pausedAtLocal: stamp.local,
    events: [ev, ...current.events],
  };
  return { ...roster, sessions: replaceSession(roster.sessions, next) };
}

/** Resume a paused ON duty session. No-op when not paused. */
export function resumeDuty(
  roster: DutyRoster,
  input: { crewId: string; now?: Date },
): DutyRoster {
  const now = input.now ?? appNow();
  const current = currentSession(roster.sessions, input.crewId);
  if (!current || current.status !== "on" || !current.pausedAtUtc) return roster;
  const finalized = finalizeOpenPause(current, now);
  const ev = event("resume_duty", now);
  const next: DutySession = {
    ...finalized,
    pausedAtUtc: undefined,
    pausedAtLocal: undefined,
    events: [ev, ...finalized.events],
  };
  return { ...roster, sessions: replaceSession(roster.sessions, next) };
}

/** Fold an open pause into pauseAccumMin so FDP stays frozen correctly. */
function finalizeOpenPause(session: DutySession, now: Date): DutySession {
  if (!session.pausedAtUtc) return session;
  const added = elapsedMin(session.pausedAtUtc, now);
  return {
    ...session,
    pauseAccumMin: (session.pauseAccumMin ?? 0) + added,
    pausedAtUtc: undefined,
    pausedAtLocal: undefined,
  };
}

/**
 * Restart the current duty / rest period from `now`. Does not End + Start
 * (that would flag rest-short). Closed sessions and other crews stay put.
 * Flight minutes already credited on earlier sessions today are kept.
 */
export function resetDutyTimer(
  roster: DutyRoster,
  input: { crewId: string; crewName: string; now?: Date },
): DutyRoster {
  const now = input.now ?? appNow();
  const current = currentSession(roster.sessions, input.crewId);
  if (!current) return roster;
  const stamp = dualClockStamp(now);
  const ev = event("reset_timer", now);

  if (current.status === "on") {
    const next: DutySession = {
      ...current,
      startedAtUtc: stamp.utc,
      startedAtLocal: stamp.local,
      flightTimeMin: 0,
      pauseAccumMin: 0,
      pausedAtUtc: undefined,
      pausedAtLocal: undefined,
      creditedLogIds: [],
      restShort: false,
      events: [ev, ...current.events],
    };
    return { ...roster, sessions: replaceSession(roster.sessions, next) };
  }

  if (current.status === "rest" && current.restStartedAtUtc) {
    const next: DutySession = {
      ...current,
      restStartedAtUtc: stamp.utc,
      restStartedAtLocal: stamp.local,
      events: [ev, ...current.events],
    };
    return { ...roster, sessions: replaceSession(roster.sessions, next) };
  }

  return roster;
}

export function startRest(
  roster: DutyRoster,
  input: { crewId: string; crewName: string; now?: Date },
): DutyRoster {
  const now = input.now ?? appNow();
  const current = currentSession(roster.sessions, input.crewId);
  if (current?.status === "rest") return roster;
  const stamp = dualClockStamp(now);
  const ev = event("start_rest", now);

  if (current?.status === "on") {
    const cleared = finalizeOpenPause(current, now);
    const next: DutySession = {
      ...cleared,
      status: "rest",
      endedAtUtc: stamp.utc,
      endedAtLocal: stamp.local,
      restStartedAtUtc: stamp.utc,
      restStartedAtLocal: stamp.local,
      pausedAtUtc: undefined,
      pausedAtLocal: undefined,
      events: [ev, ...cleared.events],
    };
    return { ...roster, sessions: replaceSession(roster.sessions, next) };
  }

  const session: DutySession = {
    id: newDutyId("duty", now),
    crewId: input.crewId,
    crewName: input.crewName,
    status: "rest",
    startedAtUtc: stamp.utc,
    startedAtLocal: stamp.local,
    endedAtUtc: stamp.utc,
    endedAtLocal: stamp.local,
    restStartedAtUtc: stamp.utc,
    restStartedAtLocal: stamp.local,
    flightTimeMin: 0,
    pauseAccumMin: 0,
    creditedLogIds: [],
    events: [ev],
  };
  return { ...roster, sessions: [session, ...roster.sessions] };
}

export function flightMinutesForDuty(log: FlightLogEntry, mission?: Mission): number {
  const block = blockTimeMin(log);
  if (block != null && block > 0) return block;
  if (mission && mission.eteMin > 0) return mission.eteMin;
  if (log.plannedEteMin && log.plannedEteMin > 0) return log.plannedEteMin;
  return 0;
}

/** Add completed-log minutes to the open ON DUTY session for that crew. */
export function creditFlightOnComplete(
  roster: DutyRoster,
  log: FlightLogEntry,
  minutes: number,
): DutyRoster {
  if (minutes <= 0) return roster;
  const crewId = crewIdFromName(log.assignedPilot);
  const current = currentSession(roster.sessions, crewId);
  if (!current || current.status !== "on") return roster;
  if (current.creditedLogIds.includes(log.id) || current.creditedLogIds.includes(log.tripId)) {
    return roster;
  }
  const next: DutySession = {
    ...current,
    flightTimeMin: current.flightTimeMin + minutes,
    creditedLogIds: [...current.creditedLogIds, log.id],
  };
  return { ...roster, sessions: replaceSession(roster.sessions, next) };
}

/** Take a crashed flight's credited minutes back off the duty session. */
export function reverseFlightDuty(roster: DutyRoster, log: FlightLogEntry, minutes: number): DutyRoster {
  if (minutes <= 0) return roster;
  let changed = false;
  const sessions = roster.sessions.map((session) => {
    const hit = session.creditedLogIds.includes(log.id) || session.creditedLogIds.includes(log.tripId);
    if (!hit) return session;
    changed = true;
    return {
      ...session,
      flightTimeMin: Math.max(0, session.flightTimeMin - minutes),
      creditedLogIds: session.creditedLogIds.filter((id) => id !== log.id && id !== log.tripId),
    };
  });
  return changed ? { ...roster, sessions } : roster;
}

export function flightTimeTodayMin(sessions: DutySession[], crewId: string, now: Date): number {
  const today = halifaxDateKey(now);
  return sessions
    .filter((s) => s.crewId === crewId)
    .filter((s) => {
      if (s.status === "on") return true;
      return halifaxDateKey(new Date(s.startedAtUtc)) === today;
    })
    .reduce((sum, s) => sum + s.flightTimeMin, 0);
}

function ratioLevel(used: number, limit: number, warnRatio: number): DutyLevel {
  if (limit <= 0) return "ok";
  if (used >= limit) return "alert";
  if (used >= limit * warnRatio) return "warn";
  return "ok";
}

function worseLevel(a: DutyLevel, b: DutyLevel): DutyLevel {
  if (a === "alert" || b === "alert") return "alert";
  if (a === "warn" || b === "warn") return "warn";
  return "ok";
}

export function statusLabel(status: DutyStatus, paused = false): DutyView["statusLabel"] {
  if (status === "on" && paused) return "PAUSED";
  if (status === "on") return "ON DUTY";
  if (status === "rest") return "REST";
  return "OFF DUTY";
}

/** Wall-clock FDP minutes with completed pauses removed; open pause freezes the end stamp. */
export function sessionFdpElapsedMin(session: DutySession, now: Date): number {
  const restOnly = Boolean(session.restStartedAtUtc && session.restStartedAtUtc === session.startedAtUtc);
  if (restOnly) return 0;
  let fdpEnd: Date;
  if (session.status === "on") {
    fdpEnd = session.pausedAtUtc ? new Date(session.pausedAtUtc) : now;
  } else if (session.endedAtUtc) {
    fdpEnd = new Date(session.endedAtUtc);
  } else {
    fdpEnd = now;
  }
  const raw = elapsedMin(session.startedAtUtc, fdpEnd);
  return Math.max(0, raw - (session.pauseAccumMin ?? 0));
}

export function dutyViewFor(
  roster: DutyRoster,
  crew: DutyCrew,
  now: Date = appNow(),
): DutyView {
  const limits = roster.settings.limits;
  const session = currentSession(roster.sessions, crew.id) ?? latestSession(roster.sessions, crew.id);
  const status: DutyStatus = session?.status === "on" || session?.status === "rest" ? session.status : "off";
  const paused = Boolean(status === "on" && session?.pausedAtUtc);
  const lastEvent = session?.events[0];

  const fdpElapsedMin = session ? sessionFdpElapsedMin(session, now) : 0;

  const flightToday = flightTimeTodayMin(roster.sessions, crew.id, now);
  const fdpRemainMin = limits.maxFdpMin - fdpElapsedMin;
  const flightRemainMin = limits.maxFlightTimeMin - flightToday;

  let restElapsedMin: number | null = null;
  let restRemainMin: number | null = null;
  if (status === "rest" && session?.restStartedAtUtc) {
    restElapsedMin = elapsedMin(session.restStartedAtUtc, now);
    restRemainMin = limits.minRestMin - restElapsedMin;
  }

  let timeToLimitMin: number | null = null;
  if (status === "on" && !paused) {
    timeToLimitMin = Math.min(fdpRemainMin, flightRemainMin);
  } else if (status === "rest" && restRemainMin != null) {
    timeToLimitMin = restRemainMin;
  }

  let level: DutyLevel = "ok";
  if (status === "on") {
    level = worseLevel(
      ratioLevel(fdpElapsedMin, limits.maxFdpMin, limits.warnRatio),
      ratioLevel(flightToday, limits.maxFlightTimeMin, limits.warnRatio),
    );
    if (session?.restShort) level = worseLevel(level, "warn");
  } else if (status === "rest" && restElapsedMin != null) {
    if (restElapsedMin < limits.minRestMin) {
      level = restElapsedMin >= limits.minRestMin * limits.warnRatio ? "warn" : "ok";
    }
  }

  return {
    crewId: crew.id,
    crewName: crew.name,
    status,
    statusLabel: statusLabel(status, paused),
    paused,
    fdpElapsedMin,
    fdpRemainMin,
    flightTimeTodayMin: flightToday,
    flightRemainMin,
    restElapsedMin,
    restRemainMin,
    timeToLimitMin,
    level,
    restShort: Boolean(session?.restShort && status === "on"),
    lastEvent,
    session: currentSession(roster.sessions, crew.id),
    limits,
    certificateLabel: roster.settings.clockLabel?.trim() || roster.settings.certificateLabel,
  };
}

export function dutyCrews(roster: DutyRoster, profile: PilotProfile): DutyCrew[] {
  const primary: DutyCrew = { id: crewIdFromName(profile.name), name: profile.name };
  const seen = new Set([primary.id]);
  const extra: DutyCrew[] = [];
  for (const session of roster.sessions) {
    if (seen.has(session.crewId)) continue;
    seen.add(session.crewId);
    extra.push({ id: session.crewId, name: session.crewName });
  }
  return [primary, ...extra];
}

export function dutyViews(roster: DutyRoster, profile: PilotProfile, now: Date = appNow()): DutyView[] {
  return dutyCrews(roster, profile).map((crew) => dutyViewFor(roster, crew, now));
}

/** Gantt / roster: one view per crew id, first name wins. */
export function dutyViewsFor(roster: DutyRoster, crews: DutyCrew[], now: Date = appNow()): DutyView[] {
  const seen = new Set<string>();
  const list: DutyCrew[] = [];
  for (const crew of crews) {
    const id = crew.id?.trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    list.push({ id, name: crew.name?.trim() || id });
  }
  return list.map((crew) => dutyViewFor(roster, crew, now));
}

/** One-line dashboard clock: FDP + flight today, always fully visible above shortcuts. */
export function compactDutyClockLine(view: DutyView): string {
  const fdp = `FDP ${formatDutyHours(view.fdpElapsedMin)} / ${formatDutyHours(view.limits.maxFdpMin)}`;
  const flight = `Flight today ${formatDutyHours(view.flightTimeTodayMin)} / ${formatDutyHours(view.limits.maxFlightTimeMin)}`;
  return `${fdp} · ${flight}`;
}

/**
 * Home / dashboard glance only — read-only. Full controls live on Crew / Crew Profile.
 * Shape: On Duty: "Name" → timer
 */
export function homeDutyGlanceLine(view: DutyView): string {
  const name = view.crewName.trim() || "Crew";
  if (view.status === "rest") {
    return `Rest: "${name}" → ${formatDutyHours(view.restElapsedMin ?? 0)}`;
  }
  if (view.status === "on" && view.paused) {
    return `On Duty: "${name}" → PAUSED ${formatDutyHours(view.fdpElapsedMin)}`;
  }
  if (view.status === "on") {
    return `On Duty: "${name}" → ${formatDutyHours(view.fdpElapsedMin)}`;
  }
  return `Off duty: "${name}"`;
}

export function footerDutyLabel(view: DutyView): string {
  if (view.status === "on" && view.paused) {
    return `DUTY PAUSED  ${formatDutyHours(view.fdpElapsedMin)}`;
  }
  if (view.status === "on") {
    const limit =
      view.timeToLimitMin != null && view.level !== "ok"
        ? `  T${view.timeToLimitMin < 0 ? "" : "-"}${formatDutyHours(Math.abs(view.timeToLimitMin))}${view.timeToLimitMin < 0 ? " OVER" : ""}`
        : "";
    return `DUTY ON  ${formatDutyHours(view.fdpElapsedMin)}${limit}`;
  }
  if (view.status === "rest") {
    const elapsed = view.restElapsedMin != null ? `  ${formatDutyHours(view.restElapsedMin)}` : "";
    return `DUTY REST${elapsed}`;
  }
  return "DUTY OFF";
}

export function patchDutySettings(roster: DutyRoster, patch: Partial<DutySettings>): DutyRoster {
  const kindChanged = Boolean(patch.certificateKind && patch.certificateKind !== roster.settings.certificateKind);
  const nextKind = patch.certificateKind ?? roster.settings.certificateKind;
  const preset = DUTY_LIMIT_PRESETS[nextKind];
  const nextLimits = patch.limits
    ? hydrateDutyLimits({ ...(kindChanged ? preset : roster.settings.limits), ...patch.limits }, preset)
    : kindChanged
      ? { ...preset }
      : roster.settings.limits;
  return {
    ...roster,
    settings: hydrateDutySettings({
      ...roster.settings,
      ...patch,
      certificateUserSet: patch.certificateKind != null ? true : patch.certificateUserSet ?? roster.settings.certificateUserSet,
      limits: nextLimits,
    }),
  };
}
